/* ============================================================
   TV STATE MACHINE — the brain, as a pure reducer
   ------------------------------------------------------------
   This collapses the prototype's imperative tangle into one
   transition function. The reducer ONLY computes the next
   declarative state; it performs no side effects. Timers, audio
   foley, YouTube calls and CSS flashes live in the hook/effect
   layer and REACT to the state this produces.

   The reducer is built by `createTvMachine(channels)` so it can
   resolve dial positions, wrap-around, and no-signal channels
   against the live lineup while staying otherwise pure.
   ============================================================ */
import type { Channel, DisplaySettingKey, TvState } from './types'
import { queueIndexToProgramOrdinal, programOrdinalToQueueIndex, programCount as queueProgramCount } from './breakResolver'
import { GUIDE_ZOOMS, buildLiveQueue, liveSlot, nearestAiringMs, itemDurationSeconds } from './live'
import { searchGuide } from './guideSearch'

type OsdKind = 'channel' | 'banner' | 'volume'

export type TvAction =
  // power
  | { type: 'POWER_TOGGLE' }
  // tuning (channels)
  | { type: 'TUNE'; index: number; programIndex?: number } // deliberate tune (default program 0)
  | { type: 'CHANNEL_STEP'; dir: 1 | -1 }
  | { type: 'DIGIT'; num: number } // jump to dial position N (no-op if absent)
  // programs (videos within a channel)
  | { type: 'PROGRAM_STEP'; dir: 1 | -1 }
  | { type: 'SET_PROGRAM'; index: number } // sync from the player (no reset)
  // volume
  | { type: 'VOLUME_STEP'; dir: 1 | -1 }
  | { type: 'MUTE_TOGGLE' }
  | { type: 'SUBTITLES_TOGGLE' }
  // presentation
  | { type: 'SCREEN'; mode: TvState['screen'] }
  | { type: 'BUMPER_SHOW' }
  | { type: 'BUMPER_HIDE' }
  | { type: 'SET_PLAYBACK'; phase: TvState['playback'] }
  | { type: 'SET_COMPONENT'; component: string | null }
  // audio gate
  | { type: 'AWAIT_GESTURE'; on: boolean } // enter/leave the "holding, waiting for tap" state
  | { type: 'UNLOCK' } // whack / probe succeeded
  // live broadcast follow mode
  | { type: 'SET_LIVE'; on: boolean }
  | { type: 'SET_SCREEN_INTERACTIVE'; on: boolean }
  | { type: 'SET_BLOCKED'; on: boolean }
  | { type: 'CLEAR_BOOT_RESUME' }
  // guide
  | { type: 'GUIDE_TOGGLE' }
  | { type: 'GUIDE_CLOSE' }
  | { type: 'GUIDE_MOVE'; dir: 1 | -1; nowMs?: number }
  | { type: 'GUIDE_SELECT'; index: number }
  | { type: 'GUIDE_PROGRAM_STEP'; dir: 1 | -1 } // move the program cursor (no playback)
  | { type: 'GUIDE_ZOOM'; dir: 1 | -1 } // change the guide's visible time span
  | { type: 'GUIDE_SEARCH_OPEN' } // enter the search box at the top of the guide
  | { type: 'GUIDE_SEARCH_SET'; text: string } // update the live query
  | { type: 'GUIDE_SEARCH_CLOSE' } // leave search, back to the normal grid
  // remote
  | { type: 'SET_REMOTE_POS'; pos: { left: number; top: number } }
  | { type: 'SET_REMOTE_SCALE'; delta: number }
  // transient on-screen-display overlays
  | { type: 'OSD_SHOW'; kind: OsdKind }
  | { type: 'OSD_HIDE'; kind: OsdKind }
  | { type: 'OSD_OVERRIDE'; value: { num: number; label: string; confirm: boolean } | null }
  | { type: 'SET_OFF_AIR'; num: number | null } // tuned to an empty frequency
  // fullscreen + its peek/auto-hide remote
  | { type: 'FULLSCREEN_SET'; on: boolean }
  | { type: 'SET_NATIVE_FS'; on: boolean }
  | { type: 'REMOTE_FS'; level: TvState['remoteFs'] }
  | { type: 'REMOTE_SHEET'; open?: boolean }
  | { type: 'REMOTE_COLLAPSE'; collapsed?: boolean }
  // remote LCD touchscreen + display settings
  | { type: 'TOUCHSCREEN'; view: TvState['touchScreen'] }
  | { type: 'TOGGLE_SETTING'; key: DisplaySettingKey }
  | { type: 'AD_BUMPER_CONFIRM_SET'; confirm: NonNullable<TvState['adBumperConfirm']> }
  | { type: 'AD_BUMPER_CONFIRM_LABEL'; label: string }
  | { type: 'AD_BUMPER_CONFIRM_HIDE' }
  | { type: 'YOUTUBE_CLEANER'; available: boolean }
  | { type: 'EXTENSION_HINT_DISMISS' }
  | { type: 'BUG_REPORT_SET'; status: TvState['bugReport'] }
  // playback queue (video list) + shuffle
  | { type: 'SET_QUEUE'; queue: TvState['queue'] }
  | { type: 'SET_SHUFFLE'; on: boolean }

const VOL_STEP = 7

export interface TvMachine {
  reducer: (state: TvState, action: TvAction) => TvState
  /** Resolve a saved dial number to a lineup index (0 if not found). */
  indexOfNumber: (num: number) => number
  channels: Channel[]
}

export function createTvMachine(channels: Channel[]): TvMachine {
  const count = channels.length
  const wrap = (i: number) => ((i % count) + count) % count
  const indexOfNumber = (num: number) => {
    const i = channels.findIndex((c) => c.number === num)
    return i < 0 ? 0 : i
  }
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

  // Embeddable program count for a channel when no live queue exists yet.
  const channelProgramCount = (chIndex: number) => {
    const ch = channels[chIndex]
    return ch ? (ch.programs || []).filter((p) => p.embeddable).length : 0
  }
  const programCount = (state: TvState, chIndex: number) =>
    chIndex === state.channelIndex && state.queue.length
      ? queueProgramCount(state.queue)
      : channelProgramCount(chIndex)
  // Wrap a program index within a channel's video list (0 if it has none).
  const wrapProgram = (state: TvState, chIndex: number, i: number) => {
    const n = programCount(state, chIndex)
    return n ? ((i % n) + n) % n : 0
  }
  // Where the program cursor lands when a channel becomes selected: at the
  // current program index if it's the tuned channel (so the guide shows "what's
  // on now"), else 0. Derived from programIndex — the live now-playing position.
  const cursorFor = (state: TvState, chIndex: number) => {
    if (chIndex !== state.channelIndex) return 0
    return state.queue.length
      ? queueIndexToProgramOrdinal(state.queue, state.programIndex)
      : state.programIndex
  }

  // Where the cursor lands when the guide moves to ANOTHER channel row: the program that's airing at the
  // SAME wall-clock time as the currently-selected program's centre — i.e. keep the cursor's x-position
  // on the timeline (its "what's on at this moment") instead of snapping back to the channel's first
  // program. Both channels read the deterministic live queue (what the guide draws). Falls back to
  // cursorFor when there's no time to carry (no clock supplied, or an empty/no-signal source/target).
  const cursorByTime = (state: TvState, fromCh: number, fromProgram: number, toCh: number, nowMs?: number) => {
    const dst = channels[toCh]
    if (nowMs == null || !dst || dst.noSignal) return cursorFor(state, toCh)
    const src = channels[fromCh]
    if (!src || src.noSignal) return cursorFor(state, toCh)
    const sq = buildLiveQueue(src, state.settings.adBumpers)
    if (!sq.length) return cursorFor(state, toCh)
    const sqi = programOrdinalToQueueIndex(sq, fromProgram)
    const startMs = nearestAiringMs(sq, sqi, nowMs)
    if (startMs == null) return cursorFor(state, toCh)
    const centreMs = startMs + (itemDurationSeconds(sq[sqi]) * 1000) / 2 // the selected program's middle
    const dq = buildLiveQueue(dst, state.settings.adBumpers)
    const slot = dq.length ? liveSlot(dq, centreMs) : null
    return slot ? queueIndexToProgramOrdinal(dq, slot.index) : 0
  }

  // Loading a hidden (secret) channel UNLOCKS it forever — the one place every channel-load action
  // (TUNE, DIGIT, …) routes its unlock through. Idempotent.
  const withUnlock = (unlocked: number[], chIndex: number): number[] => {
    const ch = channels[chIndex]
    return ch && ch.hidden && !unlocked.includes(ch.number) ? [...unlocked, ch.number] : unlocked
  }

  function reducer(state: TvState, action: TvAction): TvState {
    switch (action.type) {
      case 'POWER_TOGGLE': {
        const power = state.power === 'on' ? 'off' : 'on'
        // Presentation (screen mode, warm-up/collapse) is timed by the method
        // layer; the reducer only flips power + closes the guide on power-off.
        // power-off also drops any mounted component segment (e.g. HIDEO) so it can't linger on a dead set.
        return power === 'off'
          ? { ...state, power, guideOpen: false, activeComponent: null }
          : { ...state, power }
      }

      case 'TUNE': {
        const channelIndex = wrap(action.index)
        // a deliberate tune starts at the given program (default the first);
        // tuning a real channel always leaves any empty-frequency state
        const programIndex = action.programIndex ?? 0
        const unlockedSecrets = withUnlock(state.unlockedSecrets ?? [], channelIndex)
        return { ...state, channelIndex, guideSel: channelIndex, programIndex, offAirNumber: null, unlockedSecrets }
      }

      case 'CHANNEL_STEP': {
        let channelIndex = state.channelIndex
        for (let i = 0; i < channels.length; i++) {
          channelIndex = wrap(channelIndex + action.dir)
          const ch = channels[channelIndex]
          if (!ch.hidden || (state.unlockedSecrets ?? []).includes(ch.number)) break
        }
        return { ...state, channelIndex, guideSel: channelIndex, programIndex: 0, offAirNumber: null }
      }

      case 'DIGIT': {
        const i = channels.findIndex((c) => c.number === action.num)
        if (i < 0) return state // no station on that dial position; effect layer shows static
        return { ...state, channelIndex: i, guideSel: i, programIndex: 0, unlockedSecrets: withUnlock(state.unlockedSecrets ?? [], i) }
      }

      case 'PROGRAM_STEP': {
        // wrap within the current channel is owned by the player; we optimistically
        // step the index and let SET_PROGRAM reconcile from the real playlist.
        return { ...state, programIndex: Math.max(0, state.programIndex + action.dir) }
      }

      case 'SET_PROGRAM':
        return action.index < 0 || action.index === state.programIndex
          ? state
          : { ...state, programIndex: action.index }

      case 'VOLUME_STEP':
        return {
          ...state,
          muted: false,
          volume: clamp(state.volume + action.dir * VOL_STEP, 0, 100),
        }

      case 'MUTE_TOGGLE':
        return { ...state, muted: !state.muted }

      case 'SUBTITLES_TOGGLE':
        return { ...state, subtitles: !state.subtitles }

      case 'SCREEN':
        return state.screen === action.mode ? state : { ...state, screen: action.mode }

      case 'BUMPER_SHOW':
        return state.bumper ? state : { ...state, bumper: true }

      case 'BUMPER_HIDE':
        return state.bumper ? { ...state, bumper: false } : state

      case 'SET_PLAYBACK':
        return state.playback === action.phase ? state : { ...state, playback: action.phase }

      case 'SET_COMPONENT':
        return state.activeComponent === action.component ? state : { ...state, activeComponent: action.component }

      case 'AWAIT_GESTURE':
        // Toggle the gate between 'locked' and 'awaiting-gesture'. Never touches
        // an already-'unlocked' set (clearing the wait on an unlocked set is a no-op).
        if (action.on) return state.audio === 'locked' ? { ...state, audio: 'awaiting-gesture' } : state
        return state.audio === 'awaiting-gesture' ? { ...state, audio: 'locked' } : state

      case 'UNLOCK':
        // adopt the cued resume target as the live channel, if one is held
        if (state.bootResume) {
          const { channelIndex, programIndex } = state.bootResume
          return {
            ...state,
            audio: 'unlocked',
            channelIndex,
            guideSel: channelIndex,
            programIndex,
            bootResume: null,
          }
        }
        return { ...state, audio: 'unlocked' }

      case 'CLEAR_BOOT_RESUME':
        return state.bootResume ? { ...state, bootResume: null } : state

      case 'GUIDE_TOGGLE': {
        if (state.power === 'off') return state
        if (state.guideOpen) return { ...state, guideOpen: false, guideSearch: null, guideSearchSel: 0 }
        // open ON the tuned channel, cursor at the live program (never in search mode)
        return {
          ...state,
          guideOpen: true,
          guideSearch: null,
          guideSearchSel: 0,
          guideSel: state.channelIndex,
          guideProgram: cursorFor(state, state.channelIndex),
        }
      }

      case 'GUIDE_CLOSE':
        return state.guideOpen ? { ...state, guideOpen: false, guideSearch: null, guideSearchSel: 0 } : state

      case 'GUIDE_SEARCH_OPEN':
        return { ...state, guideSearch: '', guideSearchSel: 0 }
      case 'GUIDE_SEARCH_SET':
        return { ...state, guideSearch: action.text, guideSearchSel: 0 }
      case 'GUIDE_SEARCH_CLOSE':
        return { ...state, guideSearch: null, guideSearchSel: 0 }

      case 'GUIDE_MOVE': {
        // In SEARCH mode the up/down keys drive the result cursor, not the channel row.
        if (state.guideSearch != null) {
          if (action.dir < 0 && state.guideSearchSel <= 0) return { ...state, guideSearch: null, guideSearchSel: 0 } // up past the top → leave search
          const n = searchGuide(channels, state.unlockedSecrets ?? [], state.guideSearch).length
          return { ...state, guideSearchSel: clamp(state.guideSearchSel + action.dir, 0, Math.max(0, n - 1)) }
        }
        // up from the topmost VISIBLE channel opens the search box
        const firstVisible = channels.findIndex((c) => !c.hidden || (state.unlockedSecrets ?? []).includes(c.number))
        if (action.dir < 0 && state.guideSel === firstVisible) return { ...state, guideSearch: '', guideSearchSel: 0 }
        // move the selected channel row; the program cursor re-anchors to it
        let guideSel = state.guideSel
        for (let i = 0; i < channels.length; i++) {
          guideSel = wrap(guideSel + action.dir)
          const ch = channels[guideSel]
          if (!ch.hidden || (state.unlockedSecrets ?? []).includes(ch.number)) break
        }
        // carry the cursor's TIME position onto the new row (the program on at the same moment), not 0
        return { ...state, guideSel, guideProgram: cursorByTime(state, state.guideSel, state.guideProgram, guideSel, action.nowMs) }
      }

      case 'GUIDE_SELECT': {
        const guideSel = wrap(action.index)
        return { ...state, guideSel, guideProgram: cursorFor(state, guideSel) }
      }

      case 'GUIDE_PROGRAM_STEP':
        // scrub the cursor within the SELECTED channel's video list (no playback)
        return { ...state, guideProgram: wrapProgram(state, state.guideSel, state.guideProgram + action.dir) }

      case 'GUIDE_ZOOM':
        return { ...state, guideZoom: Math.max(0, Math.min(GUIDE_ZOOMS.length - 1, state.guideZoom + action.dir)) }

      case 'SET_REMOTE_SCALE': {
        const remoteScale = Math.round(Math.max(0.5, Math.min(1.6, state.remoteScale + action.delta)) * 100) / 100
        return remoteScale === state.remoteScale ? state : { ...state, remoteScale }
      }

      case 'SET_REMOTE_POS':
        return { ...state, remotePos: action.pos }

      case 'SET_LIVE':
        return state.live === action.on ? state : { ...state, live: action.on }

      case 'SET_SCREEN_INTERACTIVE':
        return state.screenInteractive === action.on ? state : { ...state, screenInteractive: action.on }

      case 'SET_BLOCKED':
        return state.blocked === action.on ? state : { ...state, blocked: action.on }


      case 'OSD_SHOW':
        return { ...state, osd: { ...state.osd, [action.kind]: true } }

      case 'OSD_HIDE':
        return { ...state, osd: { ...state.osd, [action.kind]: false } }

      case 'OSD_OVERRIDE':
        return { ...state, osdOverride: action.value }

      case 'SET_OFF_AIR':
        return { ...state, offAirNumber: action.num }

      case 'TOUCHSCREEN':
        return state.touchScreen === action.view ? state : { ...state, touchScreen: action.view }

      case 'TOGGLE_SETTING':
        return {
          ...state,
          settings: { ...state.settings, [action.key]: !state.settings[action.key] },
        }

      case 'AD_BUMPER_CONFIRM_SET':
        return { ...state, adBumperConfirm: action.confirm }

      case 'AD_BUMPER_CONFIRM_LABEL':
        return state.adBumperConfirm
          ? { ...state, adBumperConfirm: { ...state.adBumperConfirm, label: action.label } }
          : state

      case 'AD_BUMPER_CONFIRM_HIDE':
        return state.adBumperConfirm ? { ...state, adBumperConfirm: null } : state

      case 'YOUTUBE_CLEANER':
        return state.youtubeCleaner === action.available
          ? state
          : { ...state, youtubeCleaner: action.available }

      case 'EXTENSION_HINT_DISMISS':
        return state.extensionHintDismissed ? state : { ...state, extensionHintDismissed: true }

      case 'BUG_REPORT_SET':
        return state.bugReport === action.status ? state : { ...state, bugReport: action.status }

      case 'SET_QUEUE':
        return { ...state, queue: action.queue }

      case 'SET_SHUFFLE':
        return state.shuffle === action.on ? state : { ...state, shuffle: action.on }

      case 'FULLSCREEN_SET':
        if (state.fullscreen === action.on) return state
        // entering or leaving, the remote starts docked/hidden; leaving also clears native-fs
        return { ...state, fullscreen: action.on, remoteFs: 'hidden', nativeFs: action.on && state.nativeFs }

      case 'SET_NATIVE_FS':
        return state.nativeFs === action.on ? state : { ...state, nativeFs: action.on }

      case 'REMOTE_FS':
        return state.remoteFs === action.level ? state : { ...state, remoteFs: action.level }

      case 'REMOTE_SHEET': {
        const open = action.open ?? !state.remoteSheetOpen
        return state.remoteSheetOpen === open ? state : { ...state, remoteSheetOpen: open }
      }

      case 'REMOTE_COLLAPSE': {
        const collapsed = action.collapsed ?? !state.remoteCollapsed
        return state.remoteCollapsed === collapsed ? state : { ...state, remoteCollapsed: collapsed }
      }

      default:
        return state
    }
  }

  return { reducer, indexOfNumber, channels }
}
